<template>
  <div class="tp">
    <div v-if="!catalog" class="tp-loading">
      <v-progress-circular indeterminate size="22" width="2" class="mr-2" /> Katalog wird geladen …
    </div>

    <!-- ═══ Stufe 1: Theme-Übersicht als Karten ═══ -->
    <template v-else-if="!theme">
      <div class="tp-toolbar">
        <v-select
          v-model="group" :items="catalog.groups || ['classic']"
          dense outlined hide-details label="Editor-Typ" style="max-width: 160px"
        />
        <v-select
          v-if="(catalog.pipelines || []).length > 1"
          v-model="pipeline" :items="catalog.pipelines"
          dense outlined hide-details label="Pipeline" style="max-width: 110px"
        />
        <v-spacer />
        <span v-if="overview" class="tp-toolbar__count">{{ overview.themes.length }} Themes</span>
      </div>

      <div v-if="overviewLoading" class="tp-loading mt-4">
        <v-progress-circular indeterminate size="22" width="2" class="mr-2" /> Themes werden geladen …
      </div>
      <div v-else class="tp-cards">
        <div
          v-for="t in (overview ? overview.themes : [])" :key="t.theme"
          class="tp-card" @click="openTheme(t)"
        >
          <div class="tp-card__imgwrap">
            <img v-if="t.originalUrl" :src="t.originalUrl" class="tp-card__img">
            <div v-else class="tp-card__noimg"><v-icon>mdi-image-off-outline</v-icon></div>
          </div>
          <div class="tp-card__name">{{ t.theme }}</div>
          <div class="tp-card__badges">
            <span
              v-for="v in versionsList" :key="'b' + v"
              class="tp-badge" :class="badgeClass(t.counts['v' + v] || 0)"
            >v{{ v }} · {{ t.counts['v' + v] || 0 }}/{{ slotTotal }}</span>
          </div>
        </div>

        <!-- Neues Theme anlegen -->
        <div class="tp-card tp-card--new" @click="newTheme.show = true">
          <div class="tp-card__imgwrap tp-card__imgwrap--new">
            <v-icon large color="#7b5cd6">mdi-plus</v-icon>
          </div>
          <div class="tp-card__name">Neues Theme</div>
        </div>
      </div>
    </template>

    <!-- ═══ Stufe 2: Theme-Detail mit Matrix ═══ -->
    <template v-else>
      <div class="tp-detailhead">
        <v-btn icon @click="closeTheme"><v-icon>mdi-arrow-left</v-icon></v-btn>
        <div class="tp-detailhead__original" :title="'Original ansehen'">
          <img v-if="originalUrl" :src="originalUrl" class="tp-detailhead__img" @click="openOriginalPreview">
          <div v-else class="tp-detailhead__noimg"><v-icon small>mdi-image-off-outline</v-icon></div>
        </div>
        <div>
          <div class="tp-detailhead__title">{{ theme }}</div>
          <div class="tp-detailhead__sub">{{ group }} · {{ groupStyles.length }} Styles<template v-for="v in versionsList"> · v{{ v }} {{ counts['v' + v] || 0 }}/{{ slotTotal }}</template></div>
        </div>
        <v-spacer />
        <v-btn outlined small :disabled="!!running" @click="replaceDialog.show = true">
          <v-icon small left>mdi-image-sync-outline</v-icon> Original ersetzen
        </v-btn>
        <v-btn
          color="#7b5cd6" dark depressed small class="ml-2"
          :loading="running === 'missing'" :disabled="!!running"
          @click="generate({ overwrite: false }, 'missing')"
        >Fehlende generieren</v-btn>
        <v-btn
          outlined small color="#b3261e" class="ml-2"
          :loading="running === 'all'" :disabled="!!running"
          @click="confirmRegenerateAll"
        >Alles neu</v-btn>
      </div>

      <div v-if="originalReplaced" class="tp-result tp-result--warn">
        <v-icon small class="mr-1" color="#b06000">mdi-alert</v-icon>
        Original ersetzt — die bestehenden Beispiele zeigen noch das alte Motiv.
        <v-btn x-small depressed color="#b3261e" dark class="ml-3" :loading="running === 'all'" @click="generate({ overwrite: true }, 'all')">
          Jetzt alles neu generieren
        </v-btn>
      </div>

      <div v-if="lastRun" class="tp-result" :class="{ 'tp-result--err': lastRun.counts.error }">
        <v-icon small class="mr-1" :color="lastRun.counts.error ? '#b3261e' : '#1b7f4d'">
          {{ lastRun.counts.error ? 'mdi-alert-circle' : 'mdi-check-circle' }}
        </v-icon>
        Letzter Lauf: {{ lastRun.counts.generated || 0 }} generiert ·
        {{ lastRun.counts.copied || 0 }} kopiert · {{ lastRun.counts.skipped || 0 }} übersprungen ·
        {{ lastRun.counts.error || 0 }} Fehler
        <span v-if="firstError"> — {{ firstError }}</span>
      </div>

      <table class="tp-grid">
        <tr>
          <th class="tp-grid__style">Style</th>
          <th>Alt (Legacy)</th>
          <th v-for="v in versionsList" :key="'h' + v">
            <div class="tp-grid__vhead">
              v{{ v }}
              <v-btn
                x-small depressed :loading="running === 'col' + v" :disabled="!!running"
                @click="generate({ versions: [v], overwrite: false }, 'col' + v)"
              >Alle fehlenden</v-btn>
            </div>
          </th>
        </tr>
        <tr v-for="s in groupStyles" :key="s.id">
          <td class="tp-grid__style">
            {{ s.id }}
            <span v-if="s.versions < versionsList.length" class="tp-grid__note">Prompts bis v{{ s.versions }}</span>
          </td>
          <td>
            <div class="tp-pair">
              <div v-for="ar in ORIENTATIONS" :key="ar" class="tp-cell" :class="arClass(ar)">
                <img
                  :key="cellKey(s.id, ar, 'legacy')" :src="thumbUrl(s.id, ar, null)"
                  class="tp-cell__img" @error="markMissing(s.id, ar, 'legacy')"
                  @click="openPreview(s.id, ar, null)"
                >
                <div v-if="isMissing(s.id, ar, 'legacy')" class="tp-cell__missing">—</div>
              </div>
            </div>
          </td>
          <td v-for="v in versionsList" :key="v">
            <div class="tp-pair">
              <div v-for="ar in ORIENTATIONS" :key="ar" class="tp-cell" :class="arClass(ar)">
                <img
                  :key="cellKey(s.id, ar, v)" :src="thumbUrl(s.id, ar, v)"
                  class="tp-cell__img" @error="markMissing(s.id, ar, v)"
                  @click="openPreview(s.id, ar, v)"
                >
                <div v-if="isMissing(s.id, ar, v)" class="tp-cell__missing">fehlt</div>
                <div v-if="running === cellKey(s.id, ar, v)" class="tp-cell__busy">
                  <v-progress-circular indeterminate size="18" width="2" color="#7b5cd6" />
                </div>
                <div v-else class="tp-cell__actions">
                  <button
                    class="tp-cell__action" title="Neu generieren (mit Vergleich)" :disabled="!!running"
                    @click.stop="startCandidate(s.id, ar, v, 'generate')"
                  ><v-icon x-small>mdi-refresh</v-icon></button>
                  <button
                    class="tp-cell__action" title="Eigenes Bild einsetzen (mit Vergleich)" :disabled="!!running"
                    @click.stop="pickFile('cell', { styleId: s.id, ar, version: v })"
                  ><v-icon x-small>mdi-upload</v-icon></button>
                </div>
              </div>
            </div>
          </td>
        </tr>
      </table>
    </template>

    <input ref="fileInput" type="file" accept="image/*" style="display: none" @change="onFilePicked">

    <!-- ═══ Dialog: Neues Theme ═══ -->
    <v-dialog v-model="newTheme.show" max-width="440">
      <v-card class="tp-dlg">
        <div class="tp-dlg__title"><v-icon small class="mr-2">mdi-plus</v-icon> Neues Theme anlegen</div>
        <v-text-field
          v-model="newTheme.name" dense outlined label="Theme-Name (a–z, 0–9, - _)"
          :rules="[(v) => /^[a-z0-9_-]{2,40}$/.test(v || '') || 'Nur Kleinbuchstaben, Zahlen, - und _']"
          class="mt-3"
        />
        <div class="tp-dlg__hint">
          Das Original-Bild ist die Grundlage aller Beispiele dieses Themes. Nach dem Anlegen
          „Fehlende generieren“ klicken.
        </div>
        <v-btn outlined small class="mt-2" @click="pickFile('newTheme')">
          <v-icon small left>mdi-upload</v-icon>{{ newTheme.fileName || 'Original-Bild wählen' }}
        </v-btn>
        <div class="tp-dlg__actions">
          <v-btn text @click="newTheme.show = false">Abbrechen</v-btn>
          <v-spacer />
          <v-btn
            color="#7b5cd6" dark depressed :loading="newTheme.busy"
            :disabled="!/^[a-z0-9_-]{2,40}$/.test(newTheme.name || '') || !newTheme.imageBase64"
            @click="createTheme"
          >Anlegen</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ═══ Dialog: Original ersetzen ═══ -->
    <v-dialog v-model="replaceDialog.show" max-width="440">
      <v-card class="tp-dlg">
        <div class="tp-dlg__title"><v-icon small class="mr-2">mdi-image-sync-outline</v-icon> Original ersetzen — {{ theme }}</div>
        <div class="tp-dlg__hint tp-dlg__hint--warn">
          <v-icon x-small color="#b06000" class="mr-1">mdi-alert</v-icon>
          Alle Beispiele eines Themes basieren auf demselben Original. Nach dem Ersetzen müssen
          alle Beispiele neu generiert werden, sonst zeigen sie noch das alte Motiv.
        </div>
        <v-btn outlined small class="mt-2" @click="pickFile('replace')">
          <v-icon small left>mdi-upload</v-icon>{{ replaceDialog.fileName || 'Neues Original wählen' }}
        </v-btn>
        <div class="tp-dlg__actions">
          <v-btn text @click="replaceDialog.show = false">Abbrechen</v-btn>
          <v-spacer />
          <v-btn
            color="#b3261e" dark depressed :loading="replaceDialog.busy" :disabled="!replaceDialog.imageBase64"
            @click="replaceOriginal"
          >Ersetzen</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ═══ Kandidaten-Vergleich ═══ -->
    <v-dialog v-model="candidate.show" max-width="720" persistent>
      <v-card v-if="candidate.show" class="tp-compare">
        <div class="tp-compare__title">
          {{ candidate.styleId }} · v{{ candidate.version }} · {{ candidate.ar }} · {{ theme }}
          <span class="tp-compare__source">{{ candidate.source === 'upload' ? 'eigenes Bild' : 'neu generiert' }}</span>
        </div>
        <div class="tp-compare__row">
          <div class="tp-compare__col">
            <div class="tp-compare__label">Aktuell</div>
            <img v-if="!candidate.oldMissing" :src="candidate.oldSrc" class="tp-compare__img" @error="candidate.oldMissing = true">
            <div v-else class="tp-compare__empty">noch kein Thumbnail</div>
          </div>
          <div class="tp-compare__col">
            <div class="tp-compare__label">Kandidat</div>
            <img :src="candidate.newSrc" class="tp-compare__img tp-compare__img--new">
          </div>
        </div>
        <div class="tp-compare__actions">
          <v-btn text :loading="candidate.busy" @click="resolveCandidate('discard')">Verwerfen</v-btn>
          <v-spacer />
          <v-btn color="#1b7f4d" dark depressed :loading="candidate.busy" @click="resolveCandidate('apply')">
            <v-icon small left>mdi-check</v-icon> Übernehmen
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Großansicht -->
    <v-dialog v-model="preview.show" max-width="640">
      <v-card v-if="preview.show" class="tp-preview">
        <div class="tp-preview__title">{{ preview.title }}</div>
        <img :src="preview.src" class="tp-preview__img">
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { httpsCallable } from "firebase/functions";
import { functions as fbFunctions } from "../../../../fb";

export default {
  name: "ThemePreviewsPanel",
  data() {
    return {
      ORIENTATIONS: ["2:3", "3:2"],
      catalog: null,
      overview: null,
      overviewLoading: false,
      group: "classic",
      pipeline: null,
      theme: null,
      originalUrl: null,
      originalReplaced: false,
      running: null,
      lastRun: null,
      missing: {},
      refreshKey: 0,
      fileTarget: null,
      newTheme: { show: false, name: "", imageBase64: null, fileName: "", busy: false },
      replaceDialog: { show: false, imageBase64: null, fileName: "", busy: false },
      candidate: { show: false, styleId: null, ar: null, version: null, oldSrc: "", newSrc: "", oldMissing: false, source: "generate", busy: false },
      preview: { show: false, title: "", src: "" },
    };
  },
  computed: {
    groupStyles() {
      return (this.catalog?.styles || []).filter((s) => (s.group || "sonstige") === this.group);
    },
    versionsList() {
      return this.catalog?.versions || [1, 2];
    },
    slotTotal() {
      return this.groupStyles.length * this.ORIENTATIONS.length;
    },
    counts() {
      const t = (this.overview?.themes || []).find((x) => x.theme === this.theme);
      return t ? t.counts : { legacy: 0, v1: 0, v2: 0 };
    },
    firstError() {
      const err = (this.lastRun?.results || []).find((r) => r.action === "error");
      return err ? `${err.styleId}/${err.aspectRatio}/v${err.version}: ${err.error}` : null;
    },
  },
  watch: {
    group() {
      this.theme = null;
      this.loadOverview();
    },
  },
  async mounted() {
    try {
      this.catalog = await this.call({ action: "catalog" });
      this.pipeline = this.catalog.defaultPipeline || null;
      await this.loadOverview();
    } catch (e) {
      alert("Katalog laden fehlgeschlagen: " + (e.message || e));
    }
  },
  methods: {
    async call(params) {
      const fn = httpsCallable(fbFunctions, "themePreviewAction", { timeout: 540000 });
      const { data } = await fn(params);
      return data;
    },
    async loadOverview() {
      this.overviewLoading = true;
      try {
        this.overview = await this.call({ action: "themes", payload: { group: this.group } });
      } catch (e) {
        alert("Themes laden fehlgeschlagen: " + (e.message || e));
      } finally {
        this.overviewLoading = false;
      }
    },
    badgeClass(n) {
      if (!n) return "tp-badge--none";
      return n >= this.slotTotal ? "tp-badge--full" : "tp-badge--partial";
    },
    openTheme(t) {
      this.theme = t.theme;
      this.originalUrl = t.originalUrl;
      this.originalReplaced = false;
      this.lastRun = null;
      this.missing = {};
    },
    async closeTheme() {
      this.theme = null;
      this.lastRun = null;
      await this.loadOverview();
    },
    openOriginalPreview() {
      this.preview = { show: true, title: `Original · ${this.theme}`, src: this.originalUrl };
    },
    arClass(ar) {
      return ar === "3:2" ? "tp-cell--landscape" : "tp-cell--portrait";
    },
    thumbUrl(styleId, ar, version) {
      // Transparente Versionen liegen als WebP (Alpha) im Storage, Rest als JPG
      const style = (this.catalog?.styles || []).find((st) => st.id === styleId);
      const ext = version && (style?.transparentVersions || []).includes(version) ? "webp" : "jpg";
      const path = `app/examples/${styleId}/${ar}/${version ? `v${version}/` : ""}${this.theme}.${ext}`;
      return (
        `https://firebasestorage.googleapis.com/v0/b/${this.catalog.bucket}/o/` +
        `${encodeURIComponent(path)}?alt=media&r=${this.refreshKey}`
      );
    },
    cellKey(styleId, ar, version) {
      return `${styleId}|${ar}|${version}`;
    },
    markMissing(styleId, ar, version) {
      this.$set(this.missing, this.cellKey(styleId, ar, version), true);
    },
    isMissing(styleId, ar, version) {
      return !!this.missing[this.cellKey(styleId, ar, version)];
    },
    openPreview(styleId, ar, version) {
      if (this.isMissing(styleId, ar, version === null ? "legacy" : version)) return;
      this.preview = {
        show: true,
        title: `${styleId} · ${version ? "v" + version : "Alt (Legacy)"} · ${ar} · ${this.theme}`,
        src: this.thumbUrl(styleId, ar, version),
      };
    },
    /* ── Datei-Auswahl (Zellen-Ersatz / neues Theme / Original ersetzen) ── */
    pickFile(target, cell) {
      this.fileTarget = target === "cell" ? cell : target;
      this.$refs.fileInput.click();
    },
    onFilePicked(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!file) return;
      const target = this.fileTarget;
      const img = new Image();
      const reader = new FileReader();
      reader.onload = () => { img.src = reader.result; };
      img.onload = () => {
        const scale = Math.min(1, 2000 / Math.max(img.width, img.height));
        const cv = document.createElement("canvas");
        cv.width = Math.round(img.width * scale);
        cv.height = Math.round(img.height * scale);
        cv.getContext("2d").drawImage(img, 0, 0, cv.width, cv.height);
        const dataUrl = cv.toDataURL("image/jpeg", 0.92);
        if (target === "newTheme") {
          this.newTheme.imageBase64 = dataUrl;
          this.newTheme.fileName = file.name;
        } else if (target === "replace") {
          this.replaceDialog.imageBase64 = dataUrl;
          this.replaceDialog.fileName = file.name;
        } else {
          this.startCandidate(target.styleId, target.ar, target.version, "upload", dataUrl);
        }
      };
      reader.readAsDataURL(file);
    },
    /* ── Neues Theme / Original ersetzen ── */
    async createTheme() {
      this.newTheme.busy = true;
      try {
        const res = await this.call({
          action: "setOriginal",
          payload: { theme: this.newTheme.name, group: this.group, imageBase64: this.newTheme.imageBase64 },
        });
        this.newTheme = { show: false, name: "", imageBase64: null, fileName: "", busy: false };
        await this.loadOverview();
        this.openTheme({ theme: res.theme, originalUrl: res.originalUrl, counts: { legacy: 0, v1: 0, v2: 0 } });
      } catch (e) {
        alert("Anlegen fehlgeschlagen: " + (e.message || e));
        this.newTheme.busy = false;
      }
    },
    async replaceOriginal() {
      this.replaceDialog.busy = true;
      try {
        const res = await this.call({
          action: "setOriginal",
          payload: { theme: this.theme, group: this.group, imageBase64: this.replaceDialog.imageBase64 },
        });
        this.originalUrl = `${res.originalUrl}&r=${Date.now()}`;
        this.originalReplaced = true;
        this.replaceDialog = { show: false, imageBase64: null, fileName: "", busy: false };
      } catch (e) {
        alert("Ersetzen fehlgeschlagen: " + (e.message || e));
        this.replaceDialog.busy = false;
      }
    },
    /* ── Kandidaten-Flow ── */
    async startCandidate(styleId, ar, version, action, dataUrl) {
      const key = this.cellKey(styleId, ar, version);
      this.running = key;
      try {
        const payload = { theme: this.theme, styleId, aspectRatio: ar, version, action };
        if (this.pipeline) payload.pipeline = this.pipeline;
        if (action === "upload") payload.imageBase64 = dataUrl;
        const res = await this.call({ action: "candidate", payload });
        this.candidate = {
          show: true, styleId, ar, version,
          oldSrc: this.thumbUrl(styleId, ar, version),
          oldMissing: this.isMissing(styleId, ar, version),
          newSrc: res.candidateUrl,
          source: action, busy: false,
        };
      } catch (e) {
        alert("Kandidat fehlgeschlagen: " + (e.message || e));
      } finally {
        this.running = null;
      }
    },
    async resolveCandidate(action) {
      const c = this.candidate;
      this.candidate.busy = true;
      try {
        await this.call({
          action: "candidate",
          payload: { theme: this.theme, styleId: c.styleId, aspectRatio: c.ar, version: c.version, action },
        });
        this.candidate.show = false;
        if (action === "apply") {
          this.missing = {};
          this.refreshKey++;
        }
      } catch (e) {
        alert((action === "apply" ? "Übernehmen" : "Verwerfen") + " fehlgeschlagen: " + (e.message || e));
      } finally {
        this.candidate.busy = false;
      }
    },
    /* ── Batch-Läufe (Original kommt kanonisch aus dem Storage) ── */
    confirmRegenerateAll() {
      if (confirm(`Wirklich ALLE Beispiele für „${this.theme}“ (${this.group}) neu generieren — beide Formate, alle Versionen? Bestehende Versions-Dateien werden überschrieben, die Legacy-Dateien bleiben unangetastet.`)) {
        this.generate({ overwrite: true }, "all");
      }
    },
    async generate(extra, runKey) {
      this.running = runKey;
      try {
        const payload = { theme: this.theme, styles: this.groupStyles.map((s) => s.id), ...extra };
        if (this.pipeline) payload.pipeline = this.pipeline;
        this.lastRun = await this.call({ action: "generate", payload });
        if (!this.lastRun.counts.error) this.originalReplaced = false;
        this.missing = {};
        this.refreshKey++;
      } catch (e) {
        alert("Generierung fehlgeschlagen: " + (e.message || e));
      } finally {
        this.running = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.tp-loading {
  display: flex;
  align-items: center;
  color: rgba(0, 0, 0, 0.55);
  font-size: 14px;
}

.tp-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;

  &__count {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
  }
}

/* ── Theme-Karten ── */
.tp-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 14px;
}

.tp-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 10px;
  cursor: pointer;
  transition: border-color 0.12s, box-shadow 0.12s;

  &:hover {
    border-color: #7b5cd6;
    box-shadow: 0 2px 12px rgba(123, 92, 214, 0.12);
  }

  &__imgwrap {
    aspect-ratio: 3 / 4;
    border-radius: 8px;
    overflow: hidden;
    background: #f2f0ec;

    &--new {
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px dashed rgba(123, 92, 214, 0.5);
      background: #faf8ff;
    }
  }

  &__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &__noimg {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(0, 0, 0, 0.3);
  }

  &__name {
    font-size: 13px;
    font-weight: 600;
    margin-top: 8px;
  }

  &__badges {
    display: flex;
    gap: 6px;
    margin-top: 4px;
  }
}

.tp-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 10px;

  &--none { background: #f0efec; color: rgba(0, 0, 0, 0.4); }
  &--partial { background: #fdf3e2; color: #b06000; }
  &--full { background: #e7f4e7; color: #1b7f4d; }
}

/* ── Detail-Kopf ── */
.tp-detailhead {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;

  &__original {
    width: 46px;
    height: 60px;
    border-radius: 8px;
    overflow: hidden;
    background: #f2f0ec;
    flex-shrink: 0;
  }

  &__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    cursor: zoom-in;
  }

  &__noimg {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(0, 0, 0, 0.3);
  }

  &__title {
    font-size: 17px;
    font-weight: 700;
  }

  &__sub {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.5);
  }
}

.tp-result {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 12px;
  border-radius: 8px;
  background: #eef7ee;
  margin-bottom: 14px;

  &--err { background: #fdeeee; }
  &--warn { background: #fdf3e2; }
}

/* ── Matrix ── */
.tp-grid {
  border-collapse: collapse;
  width: 100%;

  th {
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.55);
    padding: 8px 10px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    position: sticky;
    top: 0;
    background: #fff;
    z-index: 1;
  }

  td {
    padding: 10px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    vertical-align: middle;
  }

  &__style {
    font-size: 13px;
    font-weight: 600;
    min-width: 130px;
  }

  &__note {
    display: block;
    font-size: 11px;
    font-weight: 400;
    color: rgba(0, 0, 0, 0.4);
  }

  &__vhead {
    display: flex;
    align-items: center;
    gap: 10px;
  }
}

.tp-pair {
  display: flex;
  gap: 8px;
  align-items: flex-end;
}

.tp-cell {
  position: relative;

  &--portrait { width: 76px; height: 102px; }
  &--landscape { width: 102px; height: 68px; align-self: center; }

  &__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
    background: #f2f0ec;
    display: block;
    cursor: zoom-in;
  }

  &__missing {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    color: rgba(0, 0, 0, 0.4);
    background: #f7f6f3;
    border: 1px dashed rgba(0, 0, 0, 0.15);
    border-radius: 8px;
    pointer-events: none;
  }

  &__busy {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 8px;
  }

  &__actions {
    position: absolute;
    right: 3px;
    bottom: 3px;
    display: flex;
    gap: 3px;
    opacity: 0;
    transition: opacity 0.12s;
  }

  &:hover &__actions { opacity: 1; }

  &__action {
    width: 22px;
    height: 22px;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid rgba(0, 0, 0, 0.12);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:hover { border-color: #7b5cd6; }
    &:disabled { opacity: 0.4; cursor: default; }
  }
}

/* ── Dialoge ── */
.tp-dlg {
  padding: 16px 18px;

  &__title {
    font-size: 14px;
    font-weight: 700;
    display: flex;
    align-items: center;
  }

  &__hint {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.55);
    margin-top: 6px;

    &--warn {
      background: #fdf3e2;
      color: #7a5200;
      padding: 8px 10px;
      border-radius: 8px;
    }
  }

  &__actions {
    display: flex;
    margin-top: 16px;
  }
}

.tp-compare {
  padding: 16px 18px;

  &__title {
    font-size: 14px;
    font-weight: 700;
    margin-bottom: 12px;
  }

  &__source {
    font-size: 12px;
    font-weight: 400;
    color: rgba(0, 0, 0, 0.45);
    margin-left: 8px;
  }

  &__row {
    display: flex;
    gap: 16px;
  }

  &__col {
    flex: 1;
    min-width: 0;
  }

  &__label {
    font-size: 12px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.55);
    margin-bottom: 6px;
  }

  &__img {
    width: 100%;
    border-radius: 10px;
    display: block;
    background: #f2f0ec;

    &--new { outline: 2px solid #7b5cd6; outline-offset: 2px; }
  }

  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.4);
    background: #f7f6f3;
    border: 1px dashed rgba(0, 0, 0, 0.15);
    border-radius: 10px;
  }

  &__actions {
    display: flex;
    margin-top: 14px;
  }
}

.tp-preview {
  padding: 14px;

  &__title {
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 10px;
  }

  &__img {
    width: 100%;
    border-radius: 10px;
    display: block;
  }
}
</style>
